import { Dialog, DialogContent } from "@mui/material";
import { postUserVisit } from "components/app/App.slice";
import ThemeContext from "components/app/Context";
import CloseButton from "components/common/CloseButton";
import Feedback from "components/common/Feedback";
import { setFeedbackDetails } from "components/common/Snackbar/snackbar.slice";
import Stepper from "components/common/Stepper";
import Typography from "components/common/Typography";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty, isUndefined } from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { FEEDBACK_TIMER, PlanAssigmentLimit, PlanSelectionLimit } from "utils/CommonConstants";
import { getLoggedInUserDetails } from "utils/CommonFunctions";
import { responseTypes } from "utils/Types";
import { commonApiCallParam, updatePopupHeader } from "utils/UserAnalyticsUtils";
import ActionCardDialogsContext from "../ActionCard/Context";
import { resetMyList } from "../SME/sme.slice";
import AssignmentCompletedDialog from "./AssignmentCompletedDialog";
import AssignmentDetails from "./DetailsAndConfirmation";
import AssignmentFooter from "./Footer";
import PlanAssigmentLeftWrapper from "./LeftWrapper";
import MemberSelection from "./MemberSelection";
import UserAssignment from "./MemberSelection/UserAssignment";
import PlanAssigmentDrawer from "./PlanAssigmentDrawer";
import {
  fetchInncodeGroup,
  reset,
  setAssignmentFlow,
  setCurrentStep,
  setIsErrorWhenLimitExceed,
  setIsErrorWhenPlanSelectionExceed,
  setPayloadData
} from "./PlanAssignment.slice";
import PlanSelection from "./PlanSelection";
import Preview from "./Preview";
import "./index.scss";

interface Props {
  inncodes?: any;
  users?: any;
  plans?: any;
  flow?: string;
  isTemplatePlan?: boolean;
  handleCloseDialog: () => void;
  corpUserSelection?: boolean;
  scListId?: number;
  selectAllProperties?: boolean;
  unCheckedMappings?: any;
  userSelectionFlow?: string;
  isAssignCopy?: boolean;
  open: boolean;
  sharedSearch?: string;
  isPlanProgress?: boolean;
  shouldHideTargetingFilters?: boolean;
}

const PlanAssignment = (props: Props) => {
  const {
    corpUserSelection = false,
    scListId,
    selectAllProperties,
    unCheckedMappings,
    users,
    isAssignCopy = false,
    open,
    sharedSearch = "",
    isPlanProgress = false,
    shouldHideTargetingFilters = false
  } = props;
  const dispatch = useAppDispatch();
  const translate = useTranslate();

  const containerRef = useRef<HTMLInputElement | null>(null);
  const { isOpenViewActionDialog, isOpenPlanDetailDialog } = useContext(ActionCardDialogsContext);

  const { flow, currentStep, isAssignmentSuccessful } = useAppSelector((state) => state.PlanAssignment);
  const isMobileDrawerVisible: any = useAppSelector((state) => state.PlanAssignment.isMobileDrawerVisible);
  const isErrorWhenLimitExceed = useAppSelector((state) => state.PlanAssignment?.isErrorWhenLimitExceed);
  const isErrorWhenPlanSelectionExceed = useAppSelector(
    (state) => state.PlanAssignment?.isErrorWhenPlanSelectionExceed
  );
  const myPlanDetails = useAppSelector((state) => state.planDetails.planDetails);

  const {
    payloadData: { plans }
  } = useAppSelector((state) => state.PlanAssignment);
  const { isMobileScreen } = useContext(ThemeContext);

  const [errorWhenNoUser, setErrorWhenNoUser] = useState<boolean>(false);

  useEffect(() => {
    if (currentStep < 1 && !isEmpty(flow?.[currentStep])) {
      const userDetails = getLoggedInUserDetails();
      if (!isEmpty(userDetails)) {
        const params = commonApiCallParam(true);
        const param = updatePopupHeader(`${"Plan Assignment - "}${flow?.[currentStep] || ""}`);
        params.popupHeader = param;
        dispatch(postUserVisit(params));
      }
    }
  }, [getLoggedInUserDetails, flow?.[currentStep]]);

  useEffect(() => {
    if (currentStep > 0 && !isUndefined(flow?.[currentStep])) {
      const userDetails = getLoggedInUserDetails();
      if (!isEmpty(userDetails)) {
        const params = commonApiCallParam(true);
        const param = updatePopupHeader(`${"Plan Assignment - "} ${flow?.[currentStep] || ""}`);
        params.popupHeader = param;
        dispatch(postUserVisit(params));
      }
    }
  }, [getLoggedInUserDetails, currentStep]);

  useEffect(() => {
    (async () => {
      if (scListId) {
        const data = {
          size: PlanAssigmentLimit,
          inncode: "",
          engagement: "",
          page: 0,
          view: "inncodes",
          id: scListId
        };
        const res = await dispatch(fetchInncodeGroup(data));

        if (res.payload.status !== responseTypes.success) {
          dispatch(
            setFeedbackDetails({
              shouldShow: true,
              variant: "error",
              message: res.payload.response.data.errors[0].detail
            })
          );
        } else {
          const mappings = res.payload?.mappings;
          const clonedmappings = mappings.map((item: any) => ({
            id: item.ids[0],
            hltId: item.hltIds[0],
            ...item
          }));

          if (selectAllProperties) {
            const filteredMappings = clonedmappings.filter(
              (item: any) => !unCheckedMappings.some((item1: any) => item1.id == item.id)
            );
            const allInncodeWithHltId: any = [];
            filteredMappings.forEach((item: any) => {
              if (item.hltId) {
                allInncodeWithHltId.push(item);
              }
            });
            const allMappings = allInncodeWithHltId.map((item: any) => item.id);
            dispatch(setPayloadData({ users: { mappings: allMappings, hltId: allInncodeWithHltId } }));
            setErrorWhenNoUser(!allInncodeWithHltId.length);
          } else {
            const allMappings = props.users.map((item: any) => item.id);
            dispatch(setPayloadData({ users: { mappings: allMappings, hltId: users } }));
          }
        }
      }
    })();
  }, []);

  const renderComponent = (): JSX.Element | null => {
    if (flow) {
      const UserComponent = !corpUserSelection ? MemberSelection : UserAssignment;
      switch (flow[currentStep]) {
        case "Plan Selection":
          return <PlanSelection {...props} setPayloadData={setPayloadData} plans={plans} />;
        case "Assignment Preview":
          return <Preview />;
        case "Member Selection":
          return <UserComponent {...props} />;
        case "Assignment Details":
          return <AssignmentDetails {...props} />;
        case "Confirmation":
          return <AssignmentDetails {...props} />;
        default:
          return null;
      }
    }
    return null;
  };

  useEffect(() => {
    if (props.flow === "second" && !scListId) {
      dispatch(setPayloadData({ users: [{ inncode: props.inncodes, hltId: props.users }] }));
    }
    if (props.flow === "third") {
      dispatch(setPayloadData({ plans: props.plans }));
    }
    dispatch(setAssignmentFlow({ flow: props.flow || "first", withPreview: false }));
  }, []);

  const handleStepper = (index: number) => {
    dispatch(setCurrentStep(index));
  };

  const onClose = () => {
    dispatch(reset());
    dispatch(resetMyList());
    props.handleCloseDialog();
  };

  // Open the plan details view only if it's a mobile screen and the dialog is open
  const shouldOpenViewDetails = isMobileScreen && isOpenPlanDetailDialog;

  useEffect(() => {}, []);

  return (
    <>
      {isErrorWhenLimitExceed && (
        <Feedback
          form=""
          actionStatus={{ shouldShow: isErrorWhenLimitExceed, variant: "error" }}
          message={translate("common.planAssigmentLimit", { PlanAssigmentLimit: PlanAssigmentLimit.toString() })}
          onCloseChange={() => dispatch(setIsErrorWhenLimitExceed(false))}
          isCrossHidden={true}
        />
      )}
      {isErrorWhenPlanSelectionExceed && (
        <Feedback
          form=""
          actionStatus={{ shouldShow: isErrorWhenPlanSelectionExceed, variant: "error" }}
          message={translate("common.planSelectionLimit", { PlanSelectionLimit: PlanSelectionLimit.toString() })}
          onCloseChange={() => dispatch(setIsErrorWhenPlanSelectionExceed(false))}
          isCrossHidden={true}
        />
      )}
      {isMobileDrawerVisible && isMobileScreen && <PlanAssigmentDrawer {...props} />}
      {errorWhenNoUser && (
        <Feedback
          form=""
          actionStatus={{ shouldShow: errorWhenNoUser, variant: "error", timer: FEEDBACK_TIMER }}
          message="Users are not mapped to any Inncode in the list."
          onCloseChange={() => setErrorWhenNoUser(false)}
        />
      )}
      {open && (
        <Dialog
          fullScreen
          open={true}
          onClose={onClose}
          className={`assignment-flow-component ${
            isOpenViewActionDialog || isAssignmentSuccessful || isMobileDrawerVisible || shouldOpenViewDetails
              ? "d-none"
              : ""
          }`}
        >
          <DialogContent className="content-wrapper">
            <div className="assignment-flow-container">
              <div className="plan-assignment-left-wrapper-component">
                <PlanAssigmentLeftWrapper isCross={false} {...props} />
              </div>
              <div className="right-wrapper">
                <div id="right-wrapper-container" className="right-wrapper-container">
                  <div className="heading-container">
                    <div>
                      <Typography variant="h5" showBold color={tokens.Text.General.PrimaryHeadline}>
                        Plan Assignment
                      </Typography>
                    </div>
                    <div>
                      <CloseButton handleClose={onClose} />
                    </div>
                  </div>
                  <div className="content-main-container" ref={containerRef}>
                    <div className="stepper-component">
                      <Stepper
                        setCurrentStep={handleStepper}
                        flow={flow}
                        currentStep={currentStep}
                        color={tokens.Text.General.Secondary}
                      />
                    </div>
                    <div className="component-render">{renderComponent()}</div>
                  </div>
                </div>
                <div className="action-footer">
                  <AssignmentFooter
                    flow={props.flow}
                    onClose={onClose}
                    scListId={props.scListId}
                    isTemplatePlan={props.isTemplatePlan}
                    userSelectionFlow={props.userSelectionFlow}
                    isAssignCopy={isAssignCopy}
                    sharedSearch={sharedSearch}
                    {...props}
                  />
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      {isAssignmentSuccessful && <AssignmentCompletedDialog onClose={onClose} />}
    </>
  );
};

export default PlanAssignment;
